<template>
  <tr
    class="bg-app-white dark:bg-app-dark-surface1 border-b border-app-gray-200 hover:bg-app-gray-50 dark:hover:bg-app-dark-surface3 hover:cursor-pointer"
    @click="openDetails"
  >
    <td class="p-4 text-sm font-normal text-app-gray-500">{{ format(verifier.CreatedAt) }}</td>
    <td class="p-4">
      <div class="flex items-center gap-2 text-base font-medium text-app-gray-700">
        <span>{{ verifier.verifier_id }}</span>
        <div v-if="linkedProjects.length === 0"><Icon name="exclamation-circle-solid-icon" class="text-app-error" /></div>
      </div>
    </td>

    <td class="p-4 text-sm font-normal text-app-gray-500 capitalize">{{ typeDisplayName(verifier.verifier_type) }}</td>
    <td class="p-4 text-sm font-medium text-app-gray-500 capitalize">{{ networkDisplay }}</td>
    <td class="p-4 w-[300px] relative">
      <div v-if="verifier.status === 'Processing'" class="w-fit">
        <Popover position="top" is-dark>
          <Badge :variant="statusColor(verifier.status)">{{ verifier.status }}</Badge>
          <template #popover-content>
            <div class="px-2 py-2 text-app-white text-sm text-center font-medium w-max max-w-[250px]">
              {{ networkDescription }}
            </div>
          </template>
        </Popover>
      </div>
      <Badge v-else :variant="statusColor(verifier.status)">{{ verifier.status }}</Badge>
    </td>
    <td class="p-4">
      <div v-if="linkedProjects.length === 0" class="font-bold">-</div>
      <div v-else class="flex flex-wrap">
        <div v-for="(project, idx) in linkedProjects" v-show="idx <= 3 || showMore" :key="idx" class="!-ml-1">
          <Popover position="top" is-dark>
            <Avatar size="xs" :border="true" class="capitalize text-app-white" :class="`!bg-[${colorByProjectName(project)}]`">
              {{ project[0] }}
            </Avatar>
            <template #popover-content>
              <div class="px-2 py-2 text-app-white text-sm text-center font-medium w-max max-w-[250px]">
                {{ project }}
              </div>
            </template>
          </Popover>
        </div>
        <div v-show="linkedProjects.length > 4 && !showMore" class="!-ml-1">
          <Popover position="top" is-dark>
            <Avatar size="xs" :border="true" class="text-xs capitalize text-app-white !bg-[black]" @click.stop.prevent="showMoreProjects">
              {{ `+${linkedProjects.length - 4}` }}
            </Avatar>
            <template #popover-content>
              <div class="px-2 py-2 text-app-white text-sm text-center font-medium w-max max-w-[250px]">
                {{ `Show more ${linkedProjects.length - 4} projects` }}
              </div>
            </template>
          </Popover>
        </div>
      </div>
    </td>
    <td ref="verifierAction" class="p-4 text-base font-semibold text-app-gray-700 relative" aria-hidden @click.stop="showVerifierOptions = false">
      <Icon name="dots-horizontal-icon" class="cursor-pointer" aria-hidden @click.stop="showVerifierOptions = !showVerifierOptions" />
      <TransitionPopup @trigger="showVerifierOptions = false">
        <ul
          v-show="showVerifierOptions"
          class="absolute top-[44px] border border-app-gray-100 right-4 m-0 bg-app-white dark:bg-app-dark-surface1 w-[195px] shadow-md rounded-lg z-10"
        >
          <Popover v-if="!disabled && projects.length === 0" position="left" is-dark>
            <li
              class="text-sm font-medium text-app-gray-300 flex items-center py-3 px-4 gap-2 cursor-pointer"
              aria-hidden
              @click.stop="showMoveOptions = !showMoveOptions"
            >
              <Icon name="inbox-in-solid-icon" />
              <span>Move to Project</span>
            </li>
            <template #popover-content>
              <div class="text-sm sm:text-base font-normal text-app-white w-max max-w-[250px] p-4">
                A project with the same environment needs to exist to move this verifier
              </div>
            </template>
          </Popover>
          <li
            v-if="!disabled && projects.length > 0"
            class="text-sm font-medium text-app-gray-500 flex items-center py-3 px-4 gap-2 cursor-pointer hover:bg-app-gray-100 dark:hover:bg-app-dark-surface2 hover:text-app-gray-900"
            aria-hidden
            @click.stop="showMoveOptions = !showMoveOptions"
          >
            <Icon name="inbox-in-solid-icon" />
            <span>Move to Project</span>
            <TransitionPopup @trigger="showMoveOptions = false">
              <div
                v-show="showMoveOptions"
                ref="linkedProjectAction"
                class="absolute border border-app-gray-100 left-[-204px] p-4 top-3 m-0 bg-app-white dark:bg-app-dark-surface1 w-[195px] shadow-md rounded-lg z-10"
                @click.stop=""
              >
                <div>
                  <CheckboxGroup
                    v-model="linkedProjectIds"
                    class="overflow-y-auto"
                    :class="projects.length > 5 ? 'h-[100px]' : 'h-auto'"
                    :items="projects"
                  ></CheckboxGroup>
                  <div v-if="Boolean(error)" class="text-app-red-500 text-sm mt-5">{{ error }}</div>
                  <Button
                    class="ml-auto mt-4"
                    size="xs"
                    block
                    variant="secondary"
                    :disabled="movingVerifier"
                    :loading="movingVerifier"
                    @click="moveToProject"
                  >
                    <Icon name="inbox-in-solid-icon" class="mr-2" />
                    Move Verifier
                  </Button>
                </div>
              </div>
            </TransitionPopup>
          </li>
          <li>
            <router-link
              :to="verifierRoute"
              :aria-label="`link to ${verifier.verifier_id}`"
              class="text-sm font-medium text-app-gray-500 flex items-center py-3 px-4 gap-2 cursor-pointer hover:bg-app-gray-100 dark:hover:bg-app-dark-surface2 hover:text-app-gray-900"
            >
              <Icon name="information-circle-solid-icon" />
              <span>View Details</span>
            </router-link>
          </li>
          <div v-if="!disabled">
            <Divider class="mx-0" />
            <li
              class="text-sm font-medium text-app-error flex items-center gap-2 py-3 px-4 cursor-pointer hover:bg-app-gray-100 dark:hover:bg-app-dark-surface2"
              aria-hidden
              @click="isArchiveVerifier = true"
            >
              <Icon name="archive-solid-icon" />
              <span>Archive Verifier</span>
            </li>
          </div>
        </ul>
      </TransitionPopup>
    </td>
  </tr>
  <ArchiveVerifier :verifier="verifier" :is-archive-verifier="isArchiveVerifier" @closeDialog="isArchiveVerifier = false" />
</template>

<script lang="ts">
import { Avatar } from "@toruslabs/vue-components/Avatar";
import { Badge } from "@toruslabs/vue-components/Badge";
import { CheckboxGroup } from "@toruslabs/vue-components/CheckboxGroup";
import { Divider } from "@toruslabs/vue-components/Divider";
import { Icon } from "@toruslabs/vue-components/Icon";
import { defineComponent } from "vue";

import { Button } from "@/components/Button";
import Popover from "@/components/Popover";
import TransitionPopup from "@/components/TransitionPopup";
import { projectModule, userModule, verifierModule } from "@/store";
import { getColorByName } from "@/utils/color";
import { DEPLOYED, LIVE, MAINNET, NETWORKS, PROCESSING, REJECTED, SINGLE_ID_VERIFIER, TOAST_TYPES, UPDATING } from "@/utils/enums";
import { Verifier } from "@/utils/interface";

import ArchiveVerifier from "../ArchiveVerifier";

export default defineComponent({
  name: "VerifierItem",
  components: { Avatar, Badge, Button, CheckboxGroup, Divider, Icon, ArchiveVerifier, TransitionPopup, Popover },
  props: {
    verifier: {
      type: Object as () => Verifier,
      default: () => ({}),
    },
    teamName: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showVerifierOptions: false,
    isArchiveVerifier: false,
    showMoveOptions: false,
    linkedProjectIds: [] as string[],
    movingVerifier: false,
    error: "",
    showMore: false,
  }),
  computed: {
    verifierRoute() {
      return `/organization/${this.teamName}/verifiers/${encodeURIComponent(this.verifier.verifier_id)}?network=${this.verifier.network}`;
    },
    projects() {
      const projects = projectModule.projectsGetter(this.verifier.team_id) || [];
      return projects
        .filter((project) => project.network === this.verifier.network)
        .map((project) => ({
          label: project.name,
          value: project.project_id,
          id: project.ID.toString(),
        }));
    },
    networkDisplay() {
      const network = NETWORKS.find((x) => x.id === this.verifier.network);
      return network ? network.name : this.verifier.network;
    },
    networkDescription() {
      if (this.verifier.network === MAINNET) return "On average it will take up to 2 days to process this";
      return " On average it will take up to 30 minutes to process this";
    },
    linkedProjects() {
      const projectIds = this.verifier.linked_projects ? this.verifier.linked_projects.split(",") : [];
      const projectNames = [];
      for (let i = 0; i < projectIds.length; i += 1) {
        const project = projectModule.projectGetter(this.verifier.team_id, projectIds[i]);
        if (Object.keys(project ?? {}).length > 0) projectNames.push(project.name);
      }
      return projectNames;
    },
  },
  watch: {
    showVerifierOptions(flag) {
      if (flag) {
        document.addEventListener("click", this.handleClickOutside);
      } else {
        document.removeEventListener("click", this.handleClickOutside);
      }
    },
    async showMoveOptions(flag) {
      if (flag) {
        this.linkedProjectIds = this.verifier.linked_projects ? this.verifier.linked_projects.split(",") : [];
        document.addEventListener("click", this.handleClickOutside);
      } else {
        document.removeEventListener("click", this.handleClickOutside);
      }
    },
  },
  methods: {
    handleClickOutside(event: Event) {
      if (this.$refs.verifierAction && !(this.$refs.verifierAction as HTMLElement).contains(event.target as HTMLInputElement)) {
        this.showVerifierOptions = false;
      }
      if (this.$refs.linkedProjectAction && !(this.$refs.linkedProjectAction as HTMLElement).contains(event.target as HTMLInputElement)) {
        this.showMoveOptions = false;
      }
    },
    format(date: string) {
      return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    },
    typeDisplayName(type: string) {
      if (type === SINGLE_ID_VERIFIER) return "aggregate";
      return type;
    },
    statusColor(status: string) {
      switch (status) {
        case LIVE:
          return "success";
        case PROCESSING:
        case DEPLOYED:
        case UPDATING:
          return "warning";
        case REJECTED:
          return "danger";
        default:
          return undefined;
      }
    },
    async moveToProject() {
      try {
        this.error = "";
        this.movingVerifier = true;
        await verifierModule.moveVerifierToProject({
          verifierId: this.verifier.verifier_id,
          network: this.verifier.network,
          teamId: this.verifier.team_id,
          projectIds: this.linkedProjectIds,
        });
        this.showMoveOptions = false;
        this.showVerifierOptions = false;
        userModule.setToastMessage({ message: "Verifier moved successfully", type: TOAST_TYPES.SUCCESS });
      } catch (error) {
        this.error = `Error: ${(error as Error).message}`;
      } finally {
        this.movingVerifier = false;
      }
    },
    openDetails() {
      this.$router.push(this.verifierRoute);
    },
    colorByProjectName(projectName: string) {
      return getColorByName(projectName);
    },
    showMoreProjects() {
      this.showMore = true;
    },
  },
});
</script>

<style lang="scss" scoped></style>
